import { useState, type ReactNode } from 'react';
import { Box, Tab, Tabs, styled } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { StyledSectionHeading } from './InputLabel';
import { RichTextEditorField } from './RichTextEditorField';

export type LocalizedRichTextFieldProps = {
  locales: string[];
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  label?: ReactNode;
  id: string;
  maxLength?: number;
  warnEmpty?: boolean;
  disabled?: boolean;
};

const CompactTabs = styled(Tabs)({
  minHeight: 'unset',
  width: '100%',
  '& .MuiTabs-scroller': { minHeight: 'unset' },
  '& .MuiTabs-flexContainer': { minHeight: 'unset', width: '100%' },
  '& .MuiTabs-indicator': { display: 'none' },
});

const CompactTab = styled(Tab)(({ theme }) => ({
  minHeight: 'unset',
  minWidth: 'unset',
  padding: '4px 10px',
  fontSize: '0.75rem',
  textTransform: 'none',
  flex: 1,
  '& .warn-icon': { color: theme.palette.warning.main },
  '&.Mui-selected .warn-icon': { color: 'inherit' },
}));

function isHtmlEmpty(html: string | undefined): boolean {
  if (!html) return true;
  const trimmed = html.trim();
  return trimmed === '' || /^(<p><\/p>)+$/.test(trimmed);
}

export function LocalizedRichTextField({
  locales,
  value,
  onChange,
  label,
  id,
  maxLength = 2000,
  warnEmpty = false,
  disabled = false,
}: LocalizedRichTextFieldProps) {
  const [activeLocale, setActiveLocale] = useState(locales[0]);
  const labelId = `${id}-label`;

  return (
    <Box>
      {label && (
        <StyledSectionHeading id={labelId}>
          {label}
        </StyledSectionHeading>
      )}

      {locales.length > 1 && (
        <CompactTabs
          value={activeLocale}
          onChange={(_, v: string) => setActiveLocale(v)}
          selectionFollowsFocus
          aria-label="Language"
          sx={
            disabled
              ? {
                  cursor: 'not-allowed',
                  '& .MuiTab-root': { cursor: 'not-allowed' },
                }
              : undefined
          }
        >
          {locales.map((locale) => (
            <CompactTab
              key={locale}
              value={locale}
              disabled={disabled}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {locale.toUpperCase()}
                  {warnEmpty && isHtmlEmpty(value[locale]) && (
                    <WarningAmberIcon
                      aria-label="empty"
                      className="warn-icon"
                      sx={{ fontSize: '0.875rem', flexShrink: 0 }}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </CompactTabs>
      )}

      <RichTextEditorField
        key={activeLocale}
        id={`${id}-${activeLocale}`}
        lang={activeLocale}
        value={value[activeLocale] || ''}
        onChange={(html) => onChange({ ...value, [activeLocale]: html })}
        maxLength={maxLength}
        disabled={disabled}
        aria-labelledby={labelId}
        sx={{
          mt: 4,
          '& .tiptap': { minHeight: '84px' },
        }}
      />
    </Box>
  );
}
